import { InfoCircleOutlined } from '@ant-design/icons';
import { useState } from 'react';
import styled from 'styled-components';

import { Flex, Space } from '@/components/base';
import Button from '@/components/Button/Button';
import Input from '@/components/Inputs/Input';
import PasswordInput from '@/components/Inputs/PasswordInput';
import { useModalContext } from '@/components/Modal/Modal';
import Heading from '@/components/Typography/Heading';
import Typo from '@/components/Typography/Typo';
import useColor from '@/hooks/domain/useColor';
import { useInputs } from '@/hooks/pure/useInputs';
import useWorkspaceStore from '@/stores/WorkspaceStore';
import { modalStyle } from '@/styles/style';

type SalesforceOrganizationConnectionModalProps = {
  userId: string | null;
  error: string | null;
  onSubmit: (payload: {
    userId: string;
    password: string;
    consumerKey: string;
    consumerSecret: string;
    mode: 'create' | 'update';
  }) => Promise<boolean>;
};

const SalesforceOrganizationConnectionModal = (
  props: SalesforceOrganizationConnectionModalProps,
) => {
  const color = useColor();
  const { closeModal } = useModalContext();
  const { currWorkspace } = useWorkspaceStore((state) => ({
    currWorkspace: state.currWorkspace,
  }));

  const [state, onChange] = useInputs({
    userId: props.userId || '',
    password: '',
    consumerKey: '',
    consumerSecret: '',
  });

  const [loading, setLoading] = useState(false);

  const handleConnectNewOrganization = async () => {
    if (!currWorkspace) return;
    setLoading(true);
    try {
      const result = await props.onSubmit({
        userId: state.userId,
        password: state.password,
        consumerKey: state.consumerKey,
        consumerSecret: state.consumerSecret,
        mode: props.userId ? 'update' : 'create',
      });

      if (result) {
        closeModal();
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <ConnectionModalStyle d="column">
      <Heading type="h2">Salesforce Integration</Heading>
      <Space h="8px" />
      <Typo type="label1" color={color.gray600}>
        Consumer key and consumer secret are required to connect to Salesforce.
      </Typo>
      <Space h="32px" />
      <Flex j="space-between" a="end">
        <Heading type="h3">User ID</Heading>
        {/* <div
            style={{ cursor: 'pointer' }}
            onClick={() =>
              window.open('https://developers.channel.io/docs/authentication-2')
            }
          >
            <Text type="label2" style={{ color: color.primary100 }}>
              How to create new user ID{' '}
            </Text>
            <InfoCircleOutlined style={{ color: color.primary100 }} />
          </div> */}
      </Flex>
      <Space h="12px" />
        <Input
        placeholder="type user id"
        name="userId"
        sizeType="m"
          value={state.userId}
          onChange={onChange}
          disabled={!!props.userId}
        />
      <Space h="24px" />
      <Flex j="flex-start">
        <Heading type="h3">Password</Heading>
      </Flex>
      <Space h="12px" />
      <PasswordInput
        sizeType="m"
        placeholder="type password"
        name="password"
        value={state.password}
        onChange={onChange}
        type="password"
      />
      <Space h="32px" />
      <Flex j="space-between" a="end">
        <Heading type="h3">Consumer Key</Heading>
        <div
          style={{ cursor: 'pointer' }}
          onClick={() =>
            window.open(
              'https://developer.salesforce.com/docs/platform/event-relay/guide/create-connected-app.html',
            )
          }
        >
          <Typo type="label2" style={{ color: color.primary100 }}>
            How to get your consumer key{' '}
          </Typo>
          <InfoCircleOutlined style={{ color: color.primary100 }} />
        </div>
      </Flex>
      <Space h="12px" />
      <Input
        sizeType="m"
        placeholder="type consumer key"
        name="consumerKey"
        value={state.consumerKey}
        onChange={onChange}
      />
      <Space h="24px" />
      <Flex j="flex-start">
        <Heading type="h3">Consumer Secret</Heading>
      </Flex>
      <Space h="12px" />
      <PasswordInput
        sizeType="m"
        placeholder="type consumer secret"
        name="consumerSecret"
        value={state.consumerSecret}
        onChange={onChange}
        type="password"
      />
      <Space h="24px" />
      {props.error ? (
        <Flex j="space-between">
          <Typo type="label2" style={{ color: color.red600 }}>
            {props.error}
          </Typo>
          <Button sizeType="m" onClick={handleConnectNewOrganization} isLoading={loading}>
            Connect
          </Button>
        </Flex>
      ) : (
        <Flex j="flex-end">
          <Button
            isLoading={loading}
            disabled={
              state.userId &&
              state.password &&
              state.consumerKey &&
              state.consumerSecret
                ? false
                : true
            }
            sizeType="m"
            onClick={handleConnectNewOrganization}
          >
            {props.userId ? 'Update' : 'Connect'}
          </Button>
        </Flex>
      )}
    </ConnectionModalStyle>
  );
};

const ConnectionModalStyle = styled(Flex)`
  width: 620px;
  padding: 32px;
  ${modalStyle};
`;

export default SalesforceOrganizationConnectionModal;
